import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import NavigationDropdown from "../../components/NavigationDropdown";
import NavigationPanel from "../../components/NavigationPanel";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import Footer from "../../components/Footer";
import UpdatePayment from "../../components/UpdatePayment";
import CTAs from "../../components/CTAs";
import { getCurrentUser } from "../../utils/getCurrentUser";
import { stripeService } from "../../services/stripeService";

import {
  Eye, Download, X, Search, FileText,
  ChevronLeft, ChevronRight, Mail, Edit2,
  Check, AlertCircle, Send, RefreshCw,
  Building2, ChevronDown, Link,
} from "lucide-react";

/* ─── helpers ─── */
const formatCurrency = (amount, currency = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(amount || 0);

const formatDate = (ds) => {
  if (!ds) return "—";
  return new Date(ds).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const isOverdue = (dueDate) => {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
};

const getStatusStyle = (status) => {
  const map = {
    PAID:       { background: "#F0FDF4", color: "#166534" },
    AUTHORISED: { background: "#EFF6FF", color: "#1E40AF" },
    OVERDUE:    { background: "#FEF2F2", color: "#991B1B" },
    UPLOADED:   { background: "#F5F3FF", color: "#6D28D9" },
    DRAFT:      { background: "#F1F5F9", color: "#475569" },
    VOIDED:     { background: "#F1F5F9", color: "#475569" },
  };
  return map[status] || { background: "#F1F5F9", color: "#475569" };
};

const isCurrentMonth = (inv) => {
  const now = new Date();
  const dateStr = inv.dueDate || inv.invoiceDates;
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
};

const groupInvoicesByCompany = (invoices) => {
  const map = {};
  invoices.forEach((inv) => {
    const key = inv.xeroContactId || inv.xeroContactName || inv.fileName || "unknown";
    if (!map[key]) {
      map[key] = {
        contactId:   inv.xeroContactId,
        contactName: inv.xeroContactName || inv.fileName || "Unknown",
        email:       inv.xeroContactEmail || "",
        address:     inv.xeroContactAddress || {},
        invoices:    [],
      };
    }
    map[key].invoices.push(inv);
  });
  return Object.values(map).sort((a, b) => a.contactName.localeCompare(b.contactName));
};

/* ─── Source tab logos ─── */
const QBLogo = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#2CA01C"/>
    <path d="M16 6C10.477 6 6 10.477 6 16s4.477 10 10 10 10-4.477 10-10S21.523 6 16 6zm0 3a2.5 2.5 0 0 1 2.5 2.5v1H20a1 1 0 1 1 0 2h-1.5v5H20a1 1 0 1 1 0 2h-1.5v1a2.5 2.5 0 0 1-5 0v-1H12a1 1 0 1 1 0-2h1.5v-5H12a1 1 0 1 1 0-2h1.5v-1A2.5 2.5 0 0 1 16 9zm0 2a.5.5 0 0 0-.5.5v1h1v-1A.5.5 0 0 0 16 11zm-.5 3.5v5h1v-5h-1zm0 6.5v1a.5.5 0 0 0 1 0v-1h-1z" fill="white"/>
  </svg>
);

const SageLogo = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#00DC06"/>
    <path d="M16 6C10.477 6 6 10.477 6 16s4.477 10 10 10 10-4.477 10-10S21.523 6 16 6zm4.5 13.5a.75.75 0 0 1-1.06 1.06L16 17.122l-3.44 3.44a.75.75 0 1 1-1.06-1.061L14.878 16l-3.44-3.44a.75.75 0 1 1 1.06-1.06L16 14.878l3.44-3.44a.75.75 0 1 1 1.06 1.061L17.122 16l3.44 3.44z" fill="white"/>
  </svg>
);

const BCLogo = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#0F6CBD"/>
    <path d="M8 9h16v3.2H8V9zm0 5.4h16v3.2H8v-3.2zm0 5.4h10.5v3.2H8V19.8z" fill="white"/>
  </svg>
);

const KeapLogo = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#36A635"/>
    <path d="M11 8v16M21.5 12.5L14 18.2l7.5 5.8" stroke="white" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ═══════════════════════════════════════════════════
   STATEMENT EMAIL MODAL
═══════════════════════════════════════════════════ */
const StatementEmailModal = ({ group, onClose, onSend, defaultLogo, defaultPaymentDetails }) => {
  const [step, setStep]         = useState("compose");
  const [toEmail, setToEmail]   = useState(group.email);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [subject, setSubject]   = useState(
    `Statement of Account — ${new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}`
  );
  const [bodyNote, setBodyNote] = useState(
    `Dear ${group.contactName},\n\nPlease find attached your statement of account as at ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}.\n\nIf you have any queries, please do not hesitate to get in touch.\n\nKind regards`
  );
  const [sending, setSending]   = useState(false);
  const [error, setError]       = useState("");

  const currency     = group.invoices[0]?.currency || "GBP";
  const totalBalance = group.invoices.reduce((s, i) => s + (parseFloat(i.amountDue) || 0), 0);
  const overdueAmt   = group.invoices.filter(i => isOverdue(i.dueDate)).reduce((s, i) => s + (parseFloat(i.amountDue) || 0), 0);

  const generateStatementHTML = () => {
    const stmtDate = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    const logoBlock = defaultLogo
      ? `<img src="${defaultLogo}" alt="Logo" style="max-height:100px;max-width:300px;object-fit:contain;display:block;" />`
      : `<div style="font-size:22px;font-weight:900;color:#0F172A;">STATEMENT</div>`;
    const senderAddrLines = [defaultPaymentDetails?.companyName, ...(defaultPaymentDetails?.address ? defaultPaymentDetails.address.split("\n") : [])].filter(Boolean);
    const senderAddrHTML  = senderAddrLines.map((line, i) => i === 0 ? `<div style="font-size:12px;font-weight:700;color:#0F172A;">${line}</div>` : `<div style="font-size:12px;color:#333;">${line}</div>`).join("");
    const rows = group.invoices.map((inv) => {
      const od = isOverdue(inv.dueDate);
      return `<tr><td style="padding:9px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;">${formatDate(inv.invoiceDates)}</td><td style="padding:9px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#2563EB;">Invoice # ${inv.xeroInvoiceNumber || inv.fileName || "—"}</td><td style="padding:9px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;">${formatDate(inv.dueDate)}</td><td style="padding:9px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;text-align:right;">${parseFloat(inv.amountDue||0).toFixed(2)}</td><td style="padding:9px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;text-align:right;color:#94a3b8;">0.00</td><td style="padding:9px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;text-align:right;color:${od?"#991B1B":"#1a1a1a"};font-weight:${od?"700":"400"};">${parseFloat(inv.amountDue||0).toFixed(2)}</td></tr>`;
    }).join("");
    const payBlock = (defaultPaymentDetails?.companyName || defaultPaymentDetails?.accountNo)
      ? `<div style="margin-top:16px;font-size:11px;color:#333;line-height:1.9;"><strong>Please make payments to:</strong><br/>${defaultPaymentDetails.companyName||""}${defaultPaymentDetails.accountNo ? "<br/>Account No - "+defaultPaymentDetails.accountNo : ""}${defaultPaymentDetails.sortCode ? "<br/>Sort Code - "+defaultPaymentDetails.sortCode : ""}${defaultPaymentDetails.payUrl ? `<br/><a href="${defaultPaymentDetails.payUrl}">${defaultPaymentDetails.payUrl}</a>` : ""}</div>` : "";
    return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:Arial,sans-serif;font-size:12px;background:#fff;color:#1a1a1a;padding:40px 48px;}</style></head><body><div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;"><div>${logoBlock}</div><div style="text-align:right;font-size:12px;color:#333;line-height:1.8;">${senderAddrHTML}</div></div><div style="border-top:1px solid #E2E8F0;margin-bottom:24px;"></div><div style="margin-bottom:20px;"><div style="font-size:28px;font-weight:900;color:#1a1a1a;margin-bottom:12px;">STATEMENT</div><div style="font-size:13px;font-weight:700;color:#0F172A;">${group.contactName}</div><div style="font-size:12px;color:#64748B;margin-top:2px;">As at ${stmtDate}</div></div><table style="width:100%;border-collapse:collapse;"><thead><tr style="border-top:2px solid #1a1a1a;border-bottom:2px solid #1a1a1a;"><th style="padding:8px;text-align:left;font-size:11px;font-weight:700;">Date</th><th style="padding:8px;text-align:left;font-size:11px;font-weight:700;">Activity</th><th style="padding:8px;text-align:left;font-size:11px;font-weight:700;">Due Date</th><th style="padding:8px;text-align:right;font-size:11px;font-weight:700;">Invoice Amount</th><th style="padding:8px;text-align:right;font-size:11px;font-weight:700;">Payments</th><th style="padding:8px;text-align:right;font-size:11px;font-weight:700;">Balance ${currency}</th></tr></thead><tbody>${rows}</tbody></table><div style="display:flex;justify-content:flex-end;margin-top:20px;"><div style="font-size:15px;font-weight:900;border-top:2px solid #1a1a1a;padding-top:8px;min-width:320px;display:flex;justify-content:space-between;"><span>BALANCE DUE ${currency}</span><span>${totalBalance.toFixed(2)}</span></div></div><div style="margin-top:16px;font-size:11px;color:#555;">We reserve the right to charge interest on overdue amounts at 8% above the Bank of England base rate.</div>${payBlock}</body></html>`;
  };

  const handleSend = async () => {
    if (!toEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail)) { setError("Please enter a valid email address."); return; }
    setSending(true); setStep("sending");
    try {
      await onSend({ group, toEmail, subject, bodyNote, statementHTML: generateStatementHTML(), totalBalance, currency });
      setStep("done");
    } catch (e) { setError(e.message || "Failed to send."); setStep("compose"); }
    finally { setSending(false); }
  };

  const inp = { width:"100%", padding:"10px 14px", border:"1px solid #E2E8F0", borderRadius:10, fontFamily:"DM Sans,sans-serif", fontSize:14, color:"#0F172A", outline:"none", boxSizing:"border-box", background:"#fff" };

  return (
    <>
      <style>{`
        @keyframes spin3{to{transform:rotate(360deg)}}
        @keyframes ck{0%{transform:scale(0)}100%{transform:scale(1)}}
        .sem3-ov{position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;z-index:1100;padding:24px;}
        .sem3-m{background:#fff;border-radius:20px;border:1px solid #E2E8F0;box-shadow:0 24px 80px rgba(0,0,0,.18);width:100%;max-width:620px;display:flex;flex-direction:column;max-height:90vh;overflow:hidden;}
        .sem3-m.wide{max-width:920px;}
        .sem3-hd{padding:20px 24px 16px;border-bottom:1px solid #F1F5F9;display:flex;align-items:center;justify-content:space-between;background:#FAFBFD;border-radius:20px 20px 0 0;}
        .sem3-bd{padding:24px;overflow-y:auto;display:flex;flex-direction:column;gap:16px;flex:1;}
        .sem3-ft{padding:16px 24px;border-top:1px solid #F1F5F9;display:flex;gap:10px;justify-content:flex-end;background:#FAFBFD;border-radius:0 0 20px 20px;}
        .sem3-lbl{font-size:11px;font-weight:600;color:#64748B;text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px;}
        .sem3-btn{display:inline-flex;align-items:center;gap:6px;padding:10px 20px;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:14px;font-weight:600;cursor:pointer;border:1px solid;transition:all .15s;}
        .sem3-p{background:#1E40AF;color:#fff;border-color:#1E40AF;}.sem3-p:hover{background:#1d3a9e;}.sem3-p:disabled{opacity:.5;cursor:not-allowed;}
        .sem3-s{background:#fff;color:#1E293B;border-color:#E2E8F0;}.sem3-s:hover{border-color:#2563EB;background:#EFF6FF;color:#2563EB;}
        .sem3-sum{background:#F8FAFC;border:1px solid #E2E8F0;border-radius:12px;padding:16px 20px;}
        .sem3-row{display:flex;justify-content:space-between;align-items:center;padding:4px 0;font-size:13px;}
        .sem3-tot{font-size:15px;font-weight:700;border-top:1px solid #E2E8F0;padding-top:8px;margin-top:6px;color:#0F172A;}
        .sp3{width:20px;height:20px;border:2px solid #E2E8F0;border-top-color:#2563EB;border-radius:50%;animation:spin3 .7s linear infinite;}
      `}</style>
      <div className="sem3-ov" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className={`sem3-m${step === "preview" ? " wide" : ""}`}>
          <div className="sem3-hd">
            <div>
              <div style={{ fontSize:16, fontWeight:700, color:"#0F172A", display:"flex", alignItems:"center", gap:8 }}>
                <Mail size={18} style={{ color:"#2563EB" }} />
                {step === "compose" && "Send statement"}{step === "preview" && "Preview statement"}{step === "sending" && "Sending…"}{step === "done" && "Statement sent"}
              </div>
              <div style={{ fontSize:12, color:"#64748B", marginTop:3 }}>{group.contactName} · {group.invoices.length} invoice{group.invoices.length!==1?"s":""}</div>
            </div>
            <button onClick={onClose} style={{ width:32, height:32, borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}><X size={14} style={{ color:"#64748B" }}/></button>
          </div>

          {step === "compose" && (<>
            <div className="sem3-bd">
              {overdueAmt > 0 && <div style={{ display:"flex", gap:10, padding:"12px 16px", background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:10, fontSize:13, color:"#991B1B" }}><AlertCircle size={16} style={{ flexShrink:0, marginTop:1 }}/><span>Total overdue: <strong>{formatCurrency(overdueAmt, currency)}</strong></span></div>}
              <div className="sem3-sum">
                <div style={{ fontSize:12, fontWeight:600, color:"#1E293B", marginBottom:10, display:"flex", justifyContent:"space-between" }}><span>Statement summary</span><span style={{ fontSize:11, color:"#64748B" }}>As at {new Date().toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}</span></div>
                {group.invoices.map((inv) => (
                  <div key={inv._id||inv.id} className="sem3-row">
                    <span style={{ fontFamily:"DM Mono,monospace", fontSize:12, color:"#64748B" }}>{inv.xeroInvoiceNumber||inv.fileName||"—"} · due {formatDate(inv.dueDate)}</span>
                    <span style={{ fontFamily:"DM Mono,monospace", color:"#991B1B", fontSize:12, fontWeight:600 }}>{formatCurrency(inv.amountDue, currency)}</span>
                  </div>
                ))}
                <div className="sem3-row sem3-tot"><span>Balance due</span><span style={{ fontFamily:"DM Mono,monospace", color:"#2563EB" }}>{formatCurrency(totalBalance, currency)}</span></div>
              </div>
              <div>
                <div className="sem3-lbl">Recipient email</div>
                {isEditingEmail ? (
                  <div style={{ display:"flex", gap:8 }}>
                    <input type="email" style={inp} value={toEmail} onChange={(e)=>setToEmail(e.target.value)} autoFocus/>
                    <button onClick={()=>setIsEditingEmail(false)} style={{ padding:"10px 14px", background:"#10B981", color:"#fff", border:"none", borderRadius:10, cursor:"pointer", display:"flex", alignItems:"center", gap:4, fontSize:13, fontWeight:600 }}><Check size={14}/> Save</button>
                  </div>
                ) : (
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ flex:1, padding:"10px 14px", background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:10, fontSize:14, color:toEmail?"#0F172A":"#94A3B8" }}>{toEmail||"No email on file"}</div>
                    <button onClick={()=>setIsEditingEmail(true)} style={{ padding:"8px 12px", background:"#fff", border:"1px solid #E2E8F0", borderRadius:8, cursor:"pointer", display:"flex", alignItems:"center", gap:4, fontSize:12, color:"#64748B" }}><Edit2 size={13}/> Edit</button>
                  </div>
                )}
              </div>
              <div><div className="sem3-lbl">Subject</div><input type="text" style={inp} value={subject} onChange={(e)=>setSubject(e.target.value)}/></div>
              <div><div className="sem3-lbl">Message</div><textarea style={{ ...inp, resize:"vertical", minHeight:110, lineHeight:1.6 }} value={bodyNote} onChange={(e)=>setBodyNote(e.target.value)}/></div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 16px", background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:12 }}>
                <div>
                  <div style={{ fontSize:11, color:"#64748B" }}>Attachment</div>
                  <div style={{ fontSize:13, fontWeight:600, color:"#0F172A", marginTop:2 }}>Statement_{group.contactName.replace(/\s+/g,"_")}_{new Date().toISOString().slice(0,7)}.pdf</div>
                  <div style={{ fontSize:11, color:"#64748B", marginTop:2 }}>{group.invoices.length} rows · balance {formatCurrency(totalBalance,currency)}</div>
                </div>
                <button className="sem3-btn sem3-s" style={{ fontSize:12 }} onClick={()=>setStep("preview")}><Eye size={14}/> Preview</button>
              </div>
              {error && <div style={{ color:"#DC2626", fontSize:13 }}>{error}</div>}
            </div>
            <div className="sem3-ft"><button className="sem3-btn sem3-s" onClick={onClose}>Cancel</button><button className="sem3-btn sem3-p" onClick={handleSend} disabled={sending||!toEmail}><Send size={15}/> Send statement</button></div>
          </>)}

          {step === "preview" && (<>
            <div className="sem3-bd" style={{ padding:16 }}><iframe srcDoc={generateStatementHTML()} style={{ width:"100%", height:"62vh", border:"1px solid #E2E8F0", borderRadius:10 }} title="Statement preview"/></div>
            <div className="sem3-ft"><button className="sem3-btn sem3-s" onClick={()=>setStep("compose")}><ChevronLeft size={15}/> Back</button><button className="sem3-btn sem3-p" onClick={handleSend} disabled={sending}><Send size={15}/> Send</button></div>
          </>)}

          {step === "sending" && <div className="sem3-bd" style={{ alignItems:"center", justifyContent:"center", padding:"60px 24px", gap:14 }}><div className="sp3"/><div style={{ fontSize:15, fontWeight:600, color:"#0F172A" }}>Sending statement…</div><div style={{ fontSize:13, color:"#64748B" }}>Delivering to {toEmail}</div></div>}

          {step === "done" && (<>
            <div className="sem3-bd" style={{ alignItems:"center", justifyContent:"center", padding:"48px 24px", gap:14, textAlign:"center" }}>
              <div style={{ width:64, height:64, borderRadius:"50%", background:"#F0FDF4", border:"2px solid #86EFAC", display:"flex", alignItems:"center", justifyContent:"center" }}><Check size={28} style={{ color:"#10B981" }}/></div>
              <div style={{ fontSize:18, fontWeight:700, color:"#0F172A" }}>Statement sent!</div>
              <div style={{ fontSize:13, color:"#64748B", maxWidth:340 }}>Statement for <strong>{group.contactName}</strong> · <strong>{formatCurrency(totalBalance,currency)}</strong> sent to <strong>{toEmail}</strong></div>
            </div>
            <div className="sem3-ft" style={{ justifyContent:"center" }}><button className="sem3-btn sem3-p" onClick={onClose}>Done</button></div>
          </>)}
        </div>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════
   COMPANY ROW
═══════════════════════════════════════════════════ */
const CompanyRow = ({ group, onSendStatement, onPreview, onDownload, onSendToLBA, isCheckingStripe }) => {
  const [expanded, setExpanded] = useState(false);
  const total    = group.invoices.reduce((s, i) => s + (parseFloat(i.amountDue) || 0), 0);
  const currency = group.invoices[0]?.currency || "GBP";

  return (
    <div className="co-card">
      <div className="co-header" onClick={() => setExpanded(v => !v)}>
        <div className="co-icon"><Building2 size={18} style={{ color:"#2563EB" }}/></div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:14, fontWeight:700, color:"#0F172A", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{group.contactName}</div>
          <div style={{ fontSize:12, color:"#64748B", marginTop:2 }}>
            {group.invoices.length} invoice{group.invoices.length!==1?"s":""}
            {group.email && <span style={{ marginLeft:8, color:"#94A3B8" }}>· {group.email}</span>}
          </div>
        </div>
        <div style={{ textAlign:"right", marginRight:14 }}>
          <div style={{ fontSize:16, fontWeight:700, color:"#DC2626", fontFamily:"DM Mono,monospace" }}>{formatCurrency(total,currency)}</div>
          <div style={{ fontSize:11, color:"#94A3B8", marginTop:2 }}>overdue balance</div>
        </div>
        <button className="co-stmt-btn" onClick={(e)=>{ e.stopPropagation(); onSendStatement(group); }} title="Send statement">
          <Mail size={13}/> Send statement
        </button>
        <div style={{ color:"#94A3B8", transition:"transform .2s", transform:expanded?"rotate(180deg)":"rotate(0)" }}><ChevronDown size={18}/></div>
      </div>

      {expanded && (
        <div style={{ borderTop:"1px solid #F1F5F9" }}>
          <table className="co-table">
            <thead>
              <tr>{["Invoice #","Invoice Date","Due Date","Amount Due","Status","Actions"].map((h)=><th key={h} style={{ textAlign:h==="Amount Due"?"right":"left" }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {group.invoices.map((inv) => {
                const statusStyle = getStatusStyle(inv.status||"AUTHORISED");
                return (
                  <tr key={inv._id||inv.id}>
                    <td style={{ fontFamily:"DM Mono,monospace", fontSize:13, color:"#2563EB", fontWeight:600 }}>{inv.xeroInvoiceNumber||inv.fileName||"—"}</td>
                    <td style={{ fontSize:12, color:"#475569" }}>{formatDate(inv.invoiceDates||inv.invoiceDates)}</td>
                    <td><span style={{ fontSize:12, color:"#DC2626", fontWeight:600, display:"flex", alignItems:"center", gap:4 }}><AlertCircle size={11}/>{formatDate(inv.dueDate)}</span></td>
                    <td style={{ fontFamily:"DM Mono,monospace", fontSize:13, color:"#1E293B", fontWeight:600, textAlign:"right" }}>{formatCurrency(inv.amountDue,currency)}</td>
                    <td><span style={{ ...statusStyle, display:"inline-flex", alignItems:"center", gap:4, padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700 }}>● {inv.status||"AUTHORISED"}</span></td>
                    <td>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <button className="xero-icon-btn" onClick={()=>onPreview(inv)} title="Preview"><Eye size={14} style={{ color:"#2563EB" }}/></button>
                        <button className="xero-icon-btn" onClick={()=>onDownload(inv)} title="Download"><Download size={14} style={{ color:"#059669" }}/></button>
                        <CTAs type="Primarybtn" text={isCheckingStripe?"Loading…":"Send to Pre-LBA"} width="auto" onClick={()=>onSendToLBA(inv)} disabled={isCheckingStripe}/>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ padding:"10px 20px", borderTop:"1px solid #E2E8F0", display:"flex", justifyContent:"flex-end", alignItems:"center", gap:16, background:"#FAFBFD" }}>
            <span style={{ fontSize:12, color:"#64748B" }}>Total overdue</span>
            <span style={{ fontSize:15, fontWeight:700, fontFamily:"DM Mono,monospace", color:"#DC2626" }}>{formatCurrency(total,currency)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const SectionHeader = ({ label, count, accent }) => (
  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
    <div style={{ fontSize:12, fontWeight:700, color:accent?"#1E40AF":"#64748B", textTransform:"uppercase", letterSpacing:".07em", whiteSpace:"nowrap" }}>{label}</div>
    <div style={{ flex:1, height:1, background:accent?"#DBEAFE":"#E2E8F0" }}/>
    <span style={{ fontSize:11, color:accent?"#1E40AF":"#64748B", background:accent?"#EFF6FF":"#F1F5F9", border:`1px solid ${accent?"#BFDBFE":"#E2E8F0"}`, borderRadius:20, padding:"2px 10px", fontWeight:600, whiteSpace:"nowrap" }}>{count} compan{count!==1?"ies":"y"}</span>
  </div>
);

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
const Statements = () => {
  const navigate  = useNavigate();
  const API_URL   = process.env.REACT_APP_API_URL;
  const user      = getCurrentUser();
  const getToken  = () => localStorage.getItem("authToken");

  const [isNavigationDropdownOpen, setNavigationDropdownOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);

  // ── Active source tab ──
  const [activeTab, setActiveTab] = useState("xero");

  // ── Per-source invoice data ──
  const [xeroInvoices,     setXeroInvoices]     = useState([]);
  const [sageInvoices,     setSageInvoices]      = useState([]);
  const [qbInvoices,       setQbInvoices]        = useState([]);
  const [bcInvoices,       setBcInvoices]        = useState([]);
  const [keapInvoices,     setKeapInvoices]      = useState([]);
  const [uploadedInvoices, setUploadedInvoices]  = useState([]);

  const [isLoadingXero,     setIsLoadingXero]     = useState(false);
  const [isLoadingSage,     setIsLoadingSage]      = useState(false);
  const [isLoadingQb,       setIsLoadingQb]        = useState(false);
  const [isLoadingBc,       setIsLoadingBc]        = useState(false);
  const [isLoadingKeap,     setIsLoadingKeap]      = useState(false);
  const [isLoadingUploaded, setIsLoadingUploaded]  = useState(false);

  const [isSyncingXero, setIsSyncingXero] = useState(false);
  const [isSyncingQb,   setIsSyncingQb]   = useState(false);
  const [isSyncingSage, setIsSyncingSage] = useState(false);
  const [isSyncingBc,   setIsSyncingBc]   = useState(false);
  const [isSyncingKeap, setIsSyncingKeap] = useState(false);

  const [qbConnected,   setQbConnected]   = useState(false);
  const [sageConnected, setSageConnected] = useState(false);
  const [bcConnected,   setBcConnected]   = useState(false);
  const [keapConnected, setKeapConnected] = useState(false);

  const [syncMsg,   setSyncMsg]   = useState("");
  const [syncError, setSyncError] = useState("");
  const [accessMessage, setAccessMessage] = useState("");
  const [searchQuery,   setSearchQuery]   = useState("");

  const [selectedInvoice,   setSelectedInvoice]   = useState(null);
  const [previewUrl,        setPreviewUrl]         = useState(null);
  const [isLoadingPreview,  setIsLoadingPreview]   = useState(false);

  const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
  const [isUpdatePaymentOpen, setUpdatePaymentOpen]   = useState(false);
  const [isCheckingStripe,    setIsCheckingStripe]    = useState(true);
  const [pendingInvoice,      setPendingInvoice]      = useState(null);
  const [statementTarget,     setStatementTarget]     = useState(null);

  const [defaultLogo,           setDefaultLogo]           = useState(null);
  const [defaultPaymentDetails, setDefaultPaymentDetails] = useState({ companyName:"", accountNo:"", sortCode:"", payUrl:"" });

  const closeNavigationDropdown = useCallback(() => setNavigationDropdownOpen(false), []);
  const closeLogoutConfirmation = useCallback(() => setLogoutConfirmationOpen(false), []);

  // ── Load email defaults ──
  useEffect(() => {
    fetch(`${API_URL}/api/users/email-settings`, { headers:{ Authorization:`Bearer ${getToken()}` } })
      .then(r=>r.json()).then(d=>{ if(d.success){ if(d.logoDataUrl) setDefaultLogo(d.logoDataUrl); if(d.paymentDetails) setDefaultPaymentDetails(d.paymentDetails); } }).catch(()=>{});
  }, [API_URL]);

  // ── Stripe ──
  useEffect(() => {
    stripeService.getAccountStatus().then(setStripeAccountStatus).catch(()=>setStripeAccountStatus({connected:false,onboardingComplete:false})).finally(()=>setIsCheckingStripe(false));
  }, []);

  // ── Xero fetcher ──
  const fetchXero = useCallback(async () => {
    setIsLoadingXero(true); setAccessMessage("");
    try {
      const res  = await fetch(`${API_URL}/api/xero/sync-statements`, { headers:{ "Content-Type":"application/json", Authorization:`Bearer ${getToken()}` } });
      const data = await res.json();
      if (data.success) {
        setXeroInvoices((data.invoices||[]).filter(inv=>inv.xeroPdfS3Key));
      } else { setXeroInvoices([]); setAccessMessage(data.message||"Failed to load Xero statements."); }
    } catch { setXeroInvoices([]); setAccessMessage("Error loading Xero statements."); }
    finally { setIsLoadingXero(false); }
  }, [API_URL]);

  // ── Sage fetcher ──
  const fetchSage = useCallback(async () => {
    setIsLoadingSage(true);
    try {
      const statusRes  = await fetch(`${API_URL}/api/sage/status`, { headers:{ Authorization:`Bearer ${getToken()}` } });
      const statusData = await statusRes.json();
      setSageConnected(!!(statusData.success && statusData.connected));
      if (statusData.connected) {
        const res  = await fetch(`${API_URL}/api/documents/invoices/list?source=sage`, { headers:{ Authorization:`Bearer ${getToken()}` } });
        const data = await res.json();
        setSageInvoices(data.success ? (data.invoices||[]).map(inv=>({...inv,_isSage:true})) : []);
      }
    } catch { setSageInvoices([]); }
    finally { setIsLoadingSage(false); }
  }, [API_URL]);

  // ── QuickBooks fetcher ──
  const fetchQB = useCallback(async () => {
    setIsLoadingQb(true);
    try {
      const statusRes  = await fetch(`${API_URL}/api/quickbooks/status`, { headers:{ Authorization:`Bearer ${getToken()}` } });
      const statusData = await statusRes.json();
      setQbConnected(!!(statusData.success && statusData.connected));
      if (statusData.connected) {
        const res  = await fetch(`${API_URL}/api/quickbooks/invoices`, { headers:{ Authorization:`Bearer ${getToken()}` } });
        const data = await res.json();
        setQbInvoices(data.success ? (data.invoices||[]).map(inv=>({...inv,_isQuickBooks:true})) : []);
      }
    } catch { setQbInvoices([]); }
    finally { setIsLoadingQb(false); }
  }, [API_URL]);

  // ── Business Central fetcher ──
  const fetchBC = useCallback(async () => {
    setIsLoadingBc(true);
    try {
      const statusRes  = await fetch(`${API_URL}/api/business-central/status`, { headers:{ Authorization:`Bearer ${getToken()}` } });
      const statusData = await statusRes.json();
      setBcConnected(!!(statusData.success && statusData.connected));
      if (statusData.connected) {
        const res  = await fetch(`${API_URL}/api/business-central/invoices`, { headers:{ Authorization:`Bearer ${getToken()}` } });
        const data = await res.json();
        setBcInvoices(data.success ? (data.invoices||[]).map(inv=>({...inv,_isBusinessCentral:true})) : []);
      }
    } catch { setBcInvoices([]); }
    finally { setIsLoadingBc(false); }
  }, [API_URL]);

  // ── Keap fetcher ──
  const fetchKeap = useCallback(async () => {
    setIsLoadingKeap(true);
    try {
      const statusRes  = await fetch(`${API_URL}/api/keap/status`, { headers:{ Authorization:`Bearer ${getToken()}` } });
      const statusData = await statusRes.json();
      setKeapConnected(!!(statusData.success && statusData.connected));
      if (statusData.connected) {
        const res  = await fetch(`${API_URL}/api/keap/invoices`, { headers:{ Authorization:`Bearer ${getToken()}` } });
        const data = await res.json();
        setKeapInvoices(data.success ? (data.invoices||[]).map(inv=>({...inv,_isKeap:true})) : []);
      } else { setKeapInvoices([]); }
    } catch { setKeapInvoices([]); }
    finally { setIsLoadingKeap(false); }
  }, [API_URL]);

  // ── Uploaded fetcher ──
  const fetchUploaded = useCallback(async () => {
    setIsLoadingUploaded(true);
    try {
      const res  = await fetch(`${API_URL}/api/documents/invoices/uploaded`, { headers:{ Authorization:`Bearer ${getToken()}` } });
      const data = await res.json();
      setUploadedInvoices(data.success ? (data.invoices||[]).map(inv=>({...inv,_isUploaded:true})) : []);
    } catch { setUploadedInvoices([]); }
    finally { setIsLoadingUploaded(false); }
  }, [API_URL]);

  // ── Initial load ──
  useEffect(() => { fetchXero(); fetchSage(); fetchQB(); fetchBC(); fetchKeap(); fetchUploaded(); }, []);

  // ── Sync handlers ──
  const handleSyncXero = async () => {
    setIsSyncingXero(true); setSyncMsg(""); setSyncError("");
    try {
      const res  = await fetch(`${API_URL}/api/xero/sync-statements`, { method:"POST", headers:{ "Content-Type":"application/json", Authorization:`Bearer ${getToken()}` } });
      const data = await res.json();
      if (data.success) { setSyncMsg(`Synced ${data.count||""} invoices.`); await fetchXero(); }
      else setSyncError(data.message||"Sync failed.");
    } catch { setSyncError("Sync failed."); }
    finally { setIsSyncingXero(false); }
  };

  const handleSyncQB = async () => {
    setIsSyncingQb(true); setSyncMsg(""); setSyncError("");
    try {
      const res  = await fetch(`${API_URL}/api/quickbooks/sync`, { method:"POST", headers:{ "Content-Type":"application/json", Authorization:`Bearer ${getToken()}` } });
      const data = await res.json();
      if (data.success) { setSyncMsg(`Synced ${data.count||""} invoices.`); await fetchQB(); }
      else if (data.requiresReauth) { setQbConnected(false); setSyncError("QuickBooks auth expired. Please reconnect."); }
      else setSyncError(data.message||"Sync failed.");
    } catch { setSyncError("Sync failed."); }
    finally { setIsSyncingQb(false); }
  };

  const handleSyncSage = async () => {
    setIsSyncingSage(true); setSyncMsg(""); setSyncError("");
    try {
      const res  = await fetch(`${API_URL}/api/sage/sync`, { method:"POST", headers:{ "Content-Type":"application/json", Authorization:`Bearer ${getToken()}` } });
      const data = await res.json();
      if (data.success) { setSyncMsg(`Synced ${data.count||""} invoices.`); await fetchSage(); }
      else if (data.requiresReauth) { setSageConnected(false); setSyncError("Sage auth expired. Please reconnect."); }
      else setSyncError(data.message||"Sync failed.");
    } catch { setSyncError("Sync failed."); }
    finally { setIsSyncingSage(false); }
  };

  const handleSyncBC = async () => {
    setIsSyncingBc(true); setSyncMsg(""); setSyncError("");
    try {
      const res  = await fetch(`${API_URL}/api/business-central/sync`, { method:"POST", headers:{ "Content-Type":"application/json", Authorization:`Bearer ${getToken()}` } });
      const data = await res.json();
      if (data.success) { setSyncMsg(`Synced ${data.count||""} invoices.`); await fetchBC(); }
      else if (data.requiresReauth) { setBcConnected(false); setSyncError("Business Central auth expired. Please reconnect."); }
      else setSyncError(data.message||"Sync failed.");
    } catch { setSyncError("Sync failed."); }
    finally { setIsSyncingBc(false); }
  };

  const handleSyncKeap = async () => {
    setIsSyncingKeap(true); setSyncMsg(""); setSyncError("");
    try {
      const res  = await fetch(`${API_URL}/api/keap/sync`, { method:"POST", headers:{ "Content-Type":"application/json", Authorization:`Bearer ${getToken()}` } });
      const data = await res.json();
      if (data.success) { setSyncMsg(`Synced ${data.count||""} invoices.`); await fetchKeap(); }
      else if (data.requiresReauth) { setKeapConnected(false); setSyncError("Keap auth expired. Please reconnect."); }
      else setSyncError(data.message||"Sync failed.");
    } catch { setSyncError("Sync failed."); }
    finally { setIsSyncingKeap(false); }
  };

  const handleQBConnect   = async () => { try { const res=await fetch(`${API_URL}/api/quickbooks/connect`,{headers:{Authorization:`Bearer ${getToken()}`}}); const d=await res.json(); if(d.url) window.location.href=d.url; } catch { alert("Failed to connect QuickBooks."); } };
  const handleSageConnect = async () => { try { const res=await fetch(`${API_URL}/api/sage/connect`,{headers:{Authorization:`Bearer ${getToken()}`}}); const d=await res.json(); if(d.url) window.location.href=d.url; } catch { alert("Failed to connect Sage."); } };
  const handleBCConnect   = async () => { try { const res=await fetch(`${API_URL}/api/business-central/connect`,{headers:{Authorization:`Bearer ${getToken()}`}}); const d=await res.json(); if(d.url) window.location.href=d.url; } catch { alert("Failed to connect Business Central."); } };
  const handleKeapConnect = async () => { try { const res=await fetch(`${API_URL}/api/keap/connect`,{headers:{Authorization:`Bearer ${getToken()}`}}); const d=await res.json(); if(d.url) window.location.href=d.url; } catch { alert("Failed to connect Keap."); } };

  // ── Active invoices based on tab ──
  const activeInvoices = { xero:xeroInvoices, sage:sageInvoices, quickbooks:qbInvoices, businesscentral:bcInvoices, keap:keapInvoices, uploaded:uploadedInvoices }[activeTab] || [];
  const isLoadingActive = { xero:isLoadingXero, sage:isLoadingSage, quickbooks:isLoadingQb, businesscentral:isLoadingBc, keap:isLoadingKeap, uploaded:isLoadingUploaded }[activeTab];

  // ── Grouped + filtered ──
  const allGrouped = useMemo(() => {
    const groups = groupInvoicesByCompany(activeInvoices);
    if (!searchQuery.trim()) return groups;
    const q = searchQuery.toLowerCase();
    return groups.filter(g => g.contactName.toLowerCase().includes(q) || g.email.toLowerCase().includes(q) || g.invoices.some(inv=>(inv.xeroInvoiceNumber||inv.fileName||"").toLowerCase().includes(q)));
  }, [activeInvoices, searchQuery]);

  const currentMonthGroups  = useMemo(() => allGrouped.map(g=>({...g,invoices:g.invoices.filter(isCurrentMonth)})).filter(g=>g.invoices.length>0), [allGrouped]);
  const previousMonthGroups = useMemo(() => allGrouped.map(g=>({...g,invoices:g.invoices.filter(inv=>!isCurrentMonth(inv))})).filter(g=>g.invoices.length>0), [allGrouped]);
  const totalOverdue        = useMemo(() => activeInvoices.reduce((s,inv)=>s+(parseFloat(inv.amountDue)||0),0), [activeInvoices]);
  const currency            = activeInvoices[0]?.currency || "GBP";
  const currentMonthLabel   = new Date().toLocaleDateString("en-GB",{month:"long",year:"numeric"});
  const hasAnyGroups        = currentMonthGroups.length>0 || previousMonthGroups.length>0;

  // ── PDF preview / download ──
  const getInvoicePdfUrl = async (inv) => {
    try {
      const id  = inv._id || inv.id;
      const token = getToken();
      if (inv._isUploaded) {
        const res  = await fetch(`${API_URL}/api/documents/invoices/uploaded/${id}/pdf-url`, { headers:{ Authorization:`Bearer ${token}` } });
        const data = await res.json();
        return data.success ? data.url : null;
      }
      const res  = await fetch(`${API_URL}/api/xero/invoice/${id}/pdf-url`, { headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` } });
      const data = await res.json();
      return data.success ? data.url : null;
    } catch { return null; }
  };

  const handlePreview  = async (inv) => { setSelectedInvoice(inv); setIsLoadingPreview(true); setPreviewUrl(null); const url=await getInvoicePdfUrl(inv); setPreviewUrl(url); setIsLoadingPreview(false); };
  const handleDownload = async (inv) => { const url=await getInvoicePdfUrl(inv); if(url){ const a=document.createElement("a"); a.href=url; a.download=`${inv.xeroInvoiceNumber||inv.fileName||"invoice"}.pdf`; document.body.appendChild(a); a.click(); document.body.removeChild(a); } else alert("Failed to download."); };

  // ── LBA ──
  const proceedToLBAForm = (inv) => { const addr=inv.xeroContactAddress||{}; navigate("/lbaform",{state:{id:user?.id,autoIncrementAccount:user?.autoIncrementAccount,businessName:inv.xeroContactName||"",invoiceNumber:inv.xeroInvoiceNumber||inv.fileName||"",invoiceId:inv._id||inv.id,amount:inv.amountDue||inv.totalAmounts||"",currency:inv.currency||"GBP",dueDate:inv.dueDate?new Date(inv.dueDate):null,email:inv.xeroContactEmail||"",houseName:addr.house||"",street:addr.street||"",city:addr.city||"",country:addr.country||"United Kingdom",postcode:addr.postalCode||"",source:activeTab,xeroInvoiceData:inv}}); };
  const handleSendToLBA  = (inv) => { if(!stripeAccountStatus?.connected||!stripeAccountStatus?.onboardingComplete){ setPendingInvoice(inv); setUpdatePaymentOpen(true); return; } proceedToLBAForm(inv); };
  const closeUpdatePayment = useCallback(()=>{ setUpdatePaymentOpen(false); setPendingInvoice(null); },[]);
  const handleStripeConnect = async () => { try { if(!stripeAccountStatus?.connected) window.location.href=(await stripeService.createConnectAccount()).onboardingUrl; else if(!stripeAccountStatus?.onboardingComplete) window.location.href=(await stripeService.getOnboardingLink()).onboardingUrl; else { if(pendingInvoice){ proceedToLBAForm(pendingInvoice); closeUpdatePayment(); } else window.open((await stripeService.getDashboardLink()).dashboardUrl,"_blank"); } } catch { alert("Failed to connect to Stripe."); } };

  // ── Send statement ──
  const handleSendStatement = async ({ group, toEmail, subject, bodyNote, statementHTML, totalBalance, currency: cur }) => {
    const token = getToken();
    const { jsPDF } = await import("jspdf");
    const { default: autoTable } = await import("jspdf-autotable");
    const pdf = new jsPDF({ orientation:"portrait", unit:"mm", format:"a4" });
    const margin=20; const pageWidth=210; const contentWidth=pageWidth-2*margin;
    let yPos=margin;
    const pc=[15,23,42]; const gc=[71,85,105]; const lg=[241,245,249]; const bc=[226,232,240];
    let logoBottomY=yPos+14;
    if(defaultLogo){ try { const img=new Image(); await new Promise((res,rej)=>{img.onload=res;img.onerror=rej;img.src=defaultLogo;}); const ratio=Math.min(70/img.naturalWidth,30/img.naturalHeight); const ext=defaultLogo.startsWith("data:image/png")?"PNG":"JPEG"; pdf.addImage(defaultLogo,ext,margin,yPos,img.naturalWidth*ratio,img.naturalHeight*ratio,undefined,"FAST"); logoBottomY=yPos+img.naturalHeight*ratio; } catch { pdf.setFontSize(14);pdf.setFont("helvetica","bold");pdf.setTextColor(...pc);pdf.text("STATEMENT",margin,yPos+10);logoBottomY=yPos+12; } }
    const senderLines=[defaultPaymentDetails?.companyName,...(defaultPaymentDetails?.address?defaultPaymentDetails.address.split("\n"):[])].filter(Boolean);
    let addrY=yPos;
    senderLines.forEach((line,i)=>{ if(i===0){pdf.setFontSize(8.5);pdf.setFont("helvetica","bold");pdf.setTextColor(...pc);}else{pdf.setFontSize(8);pdf.setFont("helvetica","normal");pdf.setTextColor(...gc);} pdf.text(line,pageWidth-margin,addrY+4,{align:"right"}); addrY+=4.8; });
    yPos=Math.max(logoBottomY,addrY)+6;
    pdf.setDrawColor(...bc);pdf.setLineWidth(0.2);pdf.line(margin,yPos,pageWidth-margin,yPos);yPos+=10;
    pdf.setFontSize(22);pdf.setFont("helvetica","bold");pdf.setTextColor(...pc);pdf.text("STATEMENT",margin,yPos);
    const addr=group.address||{};
    let bY=yPos+10;
    pdf.setFontSize(9.5);pdf.setFont("helvetica","bold");pdf.setTextColor(...pc);pdf.text(group.contactName,margin,bY);bY+=5;
    pdf.setFontSize(9);pdf.setFont("helvetica","normal");pdf.setTextColor(...gc);
    [addr.street,addr.city,addr.postalCode,addr.country].filter(Boolean).forEach(l=>{pdf.text(l,margin,bY);bY+=4.5;});
    pdf.setFontSize(7.5);pdf.setFont("helvetica","bold");pdf.setTextColor(...gc);pdf.text("AS AT",pageWidth-margin,yPos,{align:"right"});
    pdf.setFontSize(10);pdf.setFont("helvetica","normal");pdf.setTextColor(...pc);pdf.text(new Date().toLocaleDateString("en-GB",{day:"2-digit",month:"long",year:"numeric"}),pageWidth-margin,yPos+5,{align:"right"});
    yPos=Math.max(bY,yPos+20)+8;
    autoTable(pdf,{ startY:yPos, head:[["Date","Activity","Due Date","Invoice Amount","Payments",`Balance ${cur}`]], body:group.invoices.map(inv=>[formatDate(inv.invoiceDates),`Invoice # ${inv.xeroInvoiceNumber||inv.fileName||"—"}`,formatDate(inv.dueDate),parseFloat(inv.amountDue||0).toFixed(2),"0.00",parseFloat(inv.amountDue||0).toFixed(2)]), margin:{left:margin,right:margin}, styles:{fontSize:9,cellPadding:3,textColor:pc,lineColor:bc,lineWidth:0.1}, headStyles:{fillColor:lg,textColor:pc,fontStyle:"bold",lineColor:bc,lineWidth:0.3}, columnStyles:{3:{halign:"right"},4:{halign:"right"},5:{halign:"right"}}, theme:"grid" });
    yPos=pdf.lastAutoTable.finalY+6;
    pdf.setFontSize(11);pdf.setFont("helvetica","bold");pdf.setTextColor(...pc);pdf.text(`BALANCE DUE ${cur}`,margin+contentWidth*0.5+2,yPos+4);pdf.text(totalBalance.toFixed(2),pageWidth-margin-2,yPos+4,{align:"right"});yPos+=12;
    pdf.setFontSize(8);pdf.setFont("helvetica","normal");pdf.setTextColor(...gc);pdf.text("We reserve the right to charge interest on overdue amounts at 8% above the Bank of England base rate.",margin,yPos,{maxWidth:contentWidth});
    const pdfBlob=pdf.output("blob");
    const pdfBase64=await new Promise((resolve,reject)=>{ const reader=new FileReader(); reader.onload=()=>resolve(reader.result.split(",")[1]); reader.onerror=reject; reader.readAsDataURL(pdfBlob); });
    const res=await fetch(`${API_URL}/api/xero/send-invoice`,{ method:"POST", headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`}, body:JSON.stringify({ to:toEmail, subject, body:bodyNote, invoiceNumber:`Statement — ${group.contactName}`, pdfBase64 }) });
    const data=await res.json();
    if(!data.success) throw new Error(data.message||"Failed to send statement");
  };

  const closePreview = ()=>{ setSelectedInvoice(null); setPreviewUrl(null); setIsLoadingPreview(false); };

  const TABS = [
    { key:"xero",            label:"Xero",              count:xeroInvoices.length },
    { key:"sage",            label:"Sage",              count:sageInvoices.length },
    { key:"quickbooks",      label:"QuickBooks",        count:qbInvoices.length   },
    { key:"businesscentral", label:"Business Central",  count:bcInvoices.length   },
    { key:"keap",            label:"Keap",              count:keapInvoices.length },
    { key:"uploaded",        label:"Uploaded",          count:uploadedInvoices.length },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        html,body{background:rgb(241,245,249)!important;}
        .dd-root *{box-sizing:border-box;}
        .dd-root{font-family:'DM Sans',sans-serif;background:rgb(241,245,249)!important;min-height:100vh;}
        .stmt-stat{background:#fff;border:1px solid #E2E8F0;border-radius:14px;padding:16px 20px;flex:1;min-width:0;}
        .stmt-stat-label{font-size:11px;font-weight:600;color:#64748B;text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px;}
        .stmt-stat-val{font-size:24px;font-weight:700;color:#0F172A;}
        .co-card{background:#fff;border:1px solid #E2E8F0;border-radius:14px;overflow:hidden;transition:box-shadow .2s;}
        .co-card:hover{box-shadow:0 4px 20px rgba(0,0,0,.07);}
        .co-header{display:flex;align-items:center;gap:14px;padding:16px 20px;cursor:pointer;transition:background .15s;}
        .co-header:hover{background:#FAFBFD;}
        .co-icon{width:38px;height:38px;border-radius:10px;background:#EFF6FF;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
        .co-stmt-btn{display:inline-flex;align-items:center;gap:5px;padding:7px 13px;background:#1E40AF;color:#fff;border:none;border-radius:8px;font-family:'DM Sans',sans-serif;font-size:12px;font-weight:600;cursor:pointer;flex-shrink:0;transition:background .15s;}
        .co-stmt-btn:hover{background:#1d3a9e;}
        .co-table{width:100%;border-collapse:collapse;}
        .co-table thead tr{background:#F8FAFC;}
        .co-table th{padding:8px 16px;font-size:10px;font-weight:700;color:#64748B;text-align:left;text-transform:uppercase;letter-spacing:.04em;}
        .co-table td{padding:10px 16px;font-size:12px;color:#1E293B;border-top:1px solid #F1F5F9;}
        .co-table tr:hover td{background:#FAFBFD;}
        .xero-search{display:flex;align-items:center;gap:8px;background:#fff;border:1px solid #E2E8F0;border-radius:10px;padding:9px 14px;width:260px;transition:border-color .15s,box-shadow .15s;}
        .xero-search:focus-within{border-color:#2563EB;box-shadow:0 0 0 3px rgba(37,99,235,.1);}
        .xero-search input{border:none;outline:none;background:transparent;font-family:'DM Sans',sans-serif;font-size:13px;color:#0F172A;width:100%;}
        .xero-search input::placeholder{color:#94A3B8;}
        .xero-sync-btn{display:inline-flex;align-items:center;gap:6px;padding:9px 16px;background:#fff;border:1px solid #E2E8F0;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;color:#1E293B;cursor:pointer;transition:all .15s;white-space:nowrap;}
        .xero-sync-btn:hover:not(:disabled){border-color:#2563EB;background:#EFF6FF;color:#2563EB;}
        .xero-sync-btn:disabled{opacity:.4;cursor:not-allowed;}
        .xero-icon-btn{width:34px;height:34px;border-radius:8px;display:flex;align-items:center;justify-content:center;background:#fff;border:1px solid #E2E8F0;cursor:pointer;transition:all .15s;flex-shrink:0;}
        .xero-icon-btn:hover{border-color:#2563EB;background:#EFF6FF;}
        .xero-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:64px 24px;text-align:center;gap:12px;}
        .xero-empty-icon{width:56px;height:56px;border-radius:16px;background:#F1F5F9;display:flex;align-items:center;justify-content:center;margin-bottom:4px;}
        .xero-modal{width:90vw;height:90vh;background:#fff;border-radius:16px;display:flex;flex-direction:column;border:1px solid #E2E8F0;box-shadow:0 20px 60px rgba(0,0,0,.15);overflow:hidden;}
        .xero-modal-header{padding:18px 24px;border-bottom:1px solid #E2E8F0;display:flex;align-items:center;justify-content:space-between;background:#FAFBFD;}
        .stmt-tab-bar{display:flex;align-items:center;gap:4px;background:#fff;border:1px solid #E2E8F0;border-radius:12px;padding:4px;flex-wrap:wrap;}
        .stmt-tab{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:9px;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;cursor:pointer;border:none;background:transparent;color:#64748B;transition:all .15s;white-space:nowrap;}
        .stmt-tab.active{background:#F1F5F9;color:#0F172A;}
        .stmt-tab:hover:not(.active){background:#F8FAFC;color:#1E293B;}
        .stmt-tab-count{display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:20px;padding:0 6px;border-radius:10px;font-size:11px;font-weight:700;background:#E2E8F0;color:#475569;}
        .stmt-tab.active .stmt-tab-count{background:#fff;}
        @keyframes spin{to{transform:rotate(360deg);}}
        .spinner{width:18px;height:18px;border:2px solid #E2E8F0;border-top-color:#2563EB;border-radius:50%;animation:spin .7s linear infinite;display:inline-block;flex-shrink:0;}
        .spinner-sm{width:14px;height:14px;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;animation:spin .7s linear infinite;display:inline-block;}
      `}</style>

      <div className="dd-root w-full overflow-visible" style={{ background:"rgb(241,245,249)", minHeight:"100vh" }}>
        <NavigationPanel />

        <div style={{ position:"absolute", top:136, width:"100%", padding:"0 96px 64px", display:"flex", flexDirection:"column", gap:28 }}>

          {/* ── Page header ── */}
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:16, flexWrap:"wrap" }}>
            <div>
              <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:"#0F172A" }}>Statements</h1>
              <p style={{ margin:"4px 0 0", fontSize:14, color:"#64748B" }}>
                {isLoadingActive ? "Loading…" : `${allGrouped.length} compan${allGrouped.length!==1?"ies":"y"} · ${activeInvoices.length} invoice${activeInvoices.length!==1?"s":""}${searchQuery?" (filtered)":""}`}
              </p>
              {accessMessage && <p style={{ margin:"6px 0 0", fontSize:13, color:"#D97706" }}>{accessMessage}</p>}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div className="xero-search">
                <Search size={15} style={{ color:"#94A3B8", flexShrink:0 }}/>
                <input placeholder="Search companies…" value={searchQuery} onChange={(e)=>setSearchQuery(e.target.value)}/>
                {searchQuery && <button onClick={()=>setSearchQuery("")} style={{ background:"none", border:"none", cursor:"pointer", padding:0, display:"flex" }}><X size={14} style={{ color:"#94A3B8" }}/></button>}
              </div>
              {activeTab === "xero" && <button className="xero-sync-btn" onClick={handleSyncXero} disabled={isSyncingXero}>{isSyncingXero?<><span className="spinner-sm"/>Syncing…</>:<><RefreshCw size={14}/>Sync Xero</>}</button>}
              {activeTab === "quickbooks" && qbConnected && <button className="xero-sync-btn" onClick={handleSyncQB} disabled={isSyncingQb}>{isSyncingQb?<><span className="spinner-sm"/>Syncing…</>:<><RefreshCw size={14}/>Sync QB</>}</button>}
              {activeTab === "sage" && sageConnected && <button className="xero-sync-btn" onClick={handleSyncSage} disabled={isSyncingSage}>{isSyncingSage?<><span className="spinner-sm"/>Syncing…</>:<><RefreshCw size={14}/>Sync Sage</>}</button>}
              {activeTab === "businesscentral" && bcConnected && <button className="xero-sync-btn" onClick={handleSyncBC} disabled={isSyncingBc}>{isSyncingBc?<><span className="spinner-sm"/>Syncing…</>:<><RefreshCw size={14}/>Sync BC</>}</button>}
              {activeTab === "keap" && keapConnected && <button className="xero-sync-btn" onClick={handleSyncKeap} disabled={isSyncingKeap}>{isSyncingKeap?<><span className="spinner-sm"/>Syncing…</>:<><RefreshCw size={14}/>Sync Keap</>}</button>}
            </div>
          </div>

          {/* ── Source tabs ── */}
          <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            <div className="stmt-tab-bar">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  className={`stmt-tab${activeTab===tab.key?" active":""}`}
                  onClick={()=>{ setActiveTab(tab.key); setSearchQuery(""); setSyncMsg(""); setSyncError(""); }}
                >
                  {tab.key==="quickbooks"      && <QBLogo size={14}/>}
                  {tab.key==="sage"            && <SageLogo size={14}/>}
                  {tab.key==="businesscentral" && <BCLogo size={14}/>}
                  {tab.key==="keap"            && <KeapLogo size={14}/>}
                  {tab.key==="xero"            && <FileText size={14}/>}
                  {tab.key==="uploaded"        && <span style={{ fontSize:12 }}>↑</span>}
                  {tab.label}
                  <span className="stmt-tab-count">{tab.count}</span>
                </button>
              ))}
            </div>

            {/* connect banners */}
            {activeTab==="quickbooks" && !qbConnected && !isLoadingQb && (
              <div style={{ fontSize:13, color:"#94A3B8", display:"flex", alignItems:"center", gap:6 }}>
                <QBLogo size={13}/> Not connected —
                <button onClick={handleQBConnect} style={{ background:"none", border:"none", color:"#16A34A", fontWeight:700, fontSize:13, cursor:"pointer", padding:0, textDecoration:"underline" }}>Connect QuickBooks</button>
              </div>
            )}
            {activeTab==="sage" && !sageConnected && !isLoadingSage && (
              <div style={{ fontSize:13, color:"#94A3B8", display:"flex", alignItems:"center", gap:6 }}>
                <SageLogo size={13}/> Not connected —
                <button onClick={handleSageConnect} style={{ background:"none", border:"none", color:"#00b805", fontWeight:700, fontSize:13, cursor:"pointer", padding:0, textDecoration:"underline" }}>Connect Sage</button>
              </div>
            )}
            {activeTab==="businesscentral" && !bcConnected && !isLoadingBc && (
              <div style={{ fontSize:13, color:"#94A3B8", display:"flex", alignItems:"center", gap:6 }}>
                <BCLogo size={13}/> Not connected —
                <button onClick={handleBCConnect} style={{ background:"none", border:"none", color:"#0F6CBD", fontWeight:700, fontSize:13, cursor:"pointer", padding:0, textDecoration:"underline" }}>Connect Business Central</button>
              </div>
            )}
            {activeTab==="keap" && !keapConnected && !isLoadingKeap && (
              <div style={{ fontSize:13, color:"#94A3B8", display:"flex", alignItems:"center", gap:6 }}>
                <KeapLogo size={13}/> Not connected —
                <button onClick={handleKeapConnect} style={{ background:"none", border:"none", color:"#2E8F2D", fontWeight:700, fontSize:13, cursor:"pointer", padding:0, textDecoration:"underline" }}>Connect Keap</button>
              </div>
            )}

            {/* sync messages */}
            {syncMsg && <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#15803D", background:"#DCFCE7", border:"1px solid #86EFAC", borderRadius:8, padding:"5px 12px" }}><Check size={13}/>{syncMsg}</div>}
            {syncError && <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#991B1B", background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:8, padding:"5px 12px" }}><AlertCircle size={13}/>{syncError}</div>}
          </div>

          {/* ── Summary stats ── */}
          {!isLoadingActive && activeInvoices.length > 0 && (
            <div style={{ display:"flex", gap:14 }}>
              <div className="stmt-stat"><div className="stmt-stat-label">Companies with overdue</div><div className="stmt-stat-val">{allGrouped.length}</div></div>
              <div className="stmt-stat"><div className="stmt-stat-label">Overdue invoices</div><div className="stmt-stat-val">{activeInvoices.length}</div></div>
              <div className="stmt-stat" style={{ borderColor:"#FECACA", background:"#FFF8F8" }}>
                <div className="stmt-stat-label" style={{ color:"#991B1B" }}>Total overdue balance</div>
                <div className="stmt-stat-val" style={{ color:"#DC2626", fontFamily:"DM Mono,monospace" }}>{formatCurrency(totalOverdue,currency)}</div>
              </div>
            </div>
          )}

          {/* ── Loading ── */}
          {isLoadingActive && (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"64px 0", gap:10, color:"#64748B", fontSize:14 }}>
              <span className="spinner"/> Loading…
            </div>
          )}

          {/* ── Empty states ── */}
          {!isLoadingActive && activeInvoices.length===0 && (
            <div className="xero-empty">
              <div className="xero-empty-icon"><FileText size={24} style={{ color:"#94A3B8" }}/></div>
              <div style={{ fontSize:15, fontWeight:700, color:"#0F172A" }}>
                {activeTab==="uploaded" ? "No uploaded invoices" : activeTab==="quickbooks" && !qbConnected ? "QuickBooks not connected" : activeTab==="sage" && !sageConnected ? "Sage not connected" : activeTab==="businesscentral" && !bcConnected ? "Business Central not connected" : activeTab==="keap" && !keapConnected ? "Keap not connected" : "No overdue invoices"}
              </div>
              <div style={{ fontSize:13, color:"#64748B", maxWidth:360 }}>
                {activeTab==="uploaded" ? "Upload invoices from the Invoices page first." : activeTab==="quickbooks" && !qbConnected ? "Connect your QuickBooks account to see overdue invoices." : activeTab==="sage" && !sageConnected ? "Connect your Sage account to see overdue invoices." : activeTab==="businesscentral" && !bcConnected ? "Connect your Business Central account to see overdue invoices." : activeTab==="keap" && !keapConnected ? "Connect your Keap account to see overdue invoices." : "All invoices are up to date, or sync to fetch the latest."}
              </div>
              {activeTab==="xero" && <button className="xero-sync-btn" style={{ marginTop:8 }} onClick={handleSyncXero} disabled={isSyncingXero}>{isSyncingXero?"Syncing…":"↻ Sync from Xero"}</button>}
              {activeTab==="quickbooks" && !qbConnected && <button onClick={handleQBConnect} style={{ marginTop:8, padding:"9px 18px", background:"#16A34A", color:"#fff", border:"none", borderRadius:10, fontFamily:"DM Sans,sans-serif", fontSize:13, fontWeight:600, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:6 }}><Link size={14}/> Connect QuickBooks</button>}
              {activeTab==="sage" && !sageConnected && <button onClick={handleSageConnect} style={{ marginTop:8, padding:"9px 18px", background:"#00DC06", color:"#fff", border:"none", borderRadius:10, fontFamily:"DM Sans,sans-serif", fontSize:13, fontWeight:600, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:6 }}><Link size={14}/> Connect Sage</button>}
              {activeTab==="businesscentral" && !bcConnected && <button onClick={handleBCConnect} style={{ marginTop:8, padding:"9px 18px", background:"#0F6CBD", color:"#fff", border:"none", borderRadius:10, fontFamily:"DM Sans,sans-serif", fontSize:13, fontWeight:600, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:6 }}><Link size={14}/> Connect Business Central</button>}
              {activeTab==="businesscentral" && bcConnected && <button className="xero-sync-btn" style={{ marginTop:8 }} onClick={handleSyncBC} disabled={isSyncingBc}>{isSyncingBc?"Syncing…":"↻ Sync from Business Central"}</button>}
              {activeTab==="keap" && !keapConnected && <button onClick={handleKeapConnect} style={{ marginTop:8, padding:"9px 18px", background:"#36A635", color:"#fff", border:"none", borderRadius:10, fontFamily:"DM Sans,sans-serif", fontSize:13, fontWeight:600, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:6 }}><Link size={14}/> Connect Keap</button>}
              {activeTab==="keap" && keapConnected && <button className="xero-sync-btn" style={{ marginTop:8 }} onClick={handleSyncKeap} disabled={isSyncingKeap}>{isSyncingKeap?"Syncing…":"↻ Sync from Keap"}</button>}
            </div>
          )}

          {/* ── Company groups ── */}
          {!isLoadingActive && hasAnyGroups && (
            <div style={{ display:"flex", flexDirection:"column", gap:36 }}>
              {currentMonthGroups.length > 0 && (
                <div>
                  <SectionHeader label={currentMonthLabel} count={currentMonthGroups.length} accent={true}/>
                  <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                    {currentMonthGroups.map(group=>(
                      <CompanyRow key={(group.contactId||group.contactName)+"-cur"} group={group} onSendStatement={setStatementTarget} onPreview={handlePreview} onDownload={handleDownload} onSendToLBA={handleSendToLBA} isCheckingStripe={isCheckingStripe}/>
                    ))}
                  </div>
                </div>
              )}
              {previousMonthGroups.length > 0 && (
                <div>
                  <SectionHeader label="Previous months" count={previousMonthGroups.length} accent={false}/>
                  <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                    {previousMonthGroups.map(group=>(
                      <CompanyRow key={(group.contactId||group.contactName)+"-prev"} group={group} onSendStatement={setStatementTarget} onPreview={handlePreview} onDownload={handleDownload} onSendToLBA={handleSendToLBA} isCheckingStripe={isCheckingStripe}/>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <Footer />
        </div>
      </div>

      {statementTarget && <StatementEmailModal group={statementTarget} onClose={()=>setStatementTarget(null)} onSend={handleSendStatement} defaultLogo={defaultLogo} defaultPaymentDetails={defaultPaymentDetails}/>}

      {selectedInvoice && (
        <PortalPopup overlayColor="rgba(0,0,0,0.5)" placement="Centered" onOutsideClick={closePreview}>
          <div className="xero-modal">
            <div className="xero-modal-header">
              <div>
                <div style={{ fontSize:16, fontWeight:700, color:"#0F172A", fontFamily:"DM Mono,monospace" }}>{selectedInvoice.xeroInvoiceNumber||selectedInvoice.fileName}</div>
                <div style={{ fontSize:12, color:"#64748B", marginTop:2 }}>{selectedInvoice.xeroContactName||"Invoice"}</div>
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button className="xero-sync-btn" onClick={()=>handleDownload(selectedInvoice)}><Download size={14}/> Download</button>
                <button className="xero-icon-btn" onClick={closePreview}><X size={15} style={{ color:"#64748B" }}/></button>
              </div>
            </div>
            <div style={{ flex:1, overflow:"hidden", padding:16 }}>
              {isLoadingPreview ? (
                <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:10, color:"#64748B", fontSize:14 }}><span className="spinner"/> Loading preview…</div>
              ) : previewUrl ? (
                <iframe src={previewUrl} style={{ width:"100%", height:"100%", border:"none", borderRadius:8 }} title="Invoice preview"/>
              ) : (
                <div style={{ width:"100%", height:"100%", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12 }}>
                  <FileText size={48} style={{ color:"#CBD5E1" }}/>
                  <p style={{ color:"#64748B", fontSize:14 }}>Failed to load preview.</p>
                  <button className="xero-sync-btn" onClick={()=>handleDownload(selectedInvoice)}><Download size={14}/> Download instead</button>
                </div>
              )}
            </div>
          </div>
        </PortalPopup>
      )}

      {isUpdatePaymentOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeUpdatePayment}>
          <UpdatePayment onClose={closeUpdatePayment} onConnect={handleStripeConnect} stripeAccountStatus={stripeAccountStatus}/>
        </PortalPopup>
      )}
      {isNavigationDropdownOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeNavigationDropdown}>
          <NavigationDropdown onClose={closeNavigationDropdown}/>
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeLogoutConfirmation}>
          <LogoutConfirmation onClose={closeLogoutConfirmation}/>
        </PortalPopup>
      )}
    </>
  );
};

export default Statements;